import { useState, useEffect, createContext, useContext } from "react";
import Landing from './pages/Landing';
import Dashboard from "./pages/Dashboard";
import ValueBets from "./pages/ValueBets";
import Arbitrage from "./pages/Arbitrage";
import CLVTracker from "./pages/CLVTracker";
import BankrollManager from "./pages/BankrollManager";
import TipsterHub from "./pages/TipsterHub";
import TelegramBot from './pages/TelegramBot';
import Bookmakers from "./pages/Bookmakers";
import Login from "./pages/Login";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import BestOdds from "./pages/BestOdds";
import Middles from './pages/Middles';
import Calculators from './pages/Calculators';
import Education from './pages/Education';
import HeatMap from './pages/HeatMap';
import Profile from './pages/Profile';
import Billing from './pages/Billing';
import SharpMoney from './pages/SharpMoney';
import SmartParlay from './pages/SmartParlay';
import WinnerIntelligence from './pages/WinnerIntelligence';
import Admin from './pages/Admin';
import Account from './pages/Account';
import Upgrade from './pages/Upgrade';
import BotswanaPulse from './pages/BotswanaPulse';
import LiveOdds from './pages/LiveOdds';

// ─── API Config ───────────────────────────────────────────────────────────────
export const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001/api";

// ─── Auth Context ─────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ─── API Helper ───────────────────────────────────────────────────────────────
export async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("oe_token");
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) {
    // Handle session invalidation — another device logged in
    if (data.code === 'SESSION_INVALIDATED') {
      localStorage.removeItem("oe_token");
      setTimeout(() => {
        alert('You have been logged out because your account was accessed from another device.');
        window.location.href = '/';
      }, 100);
      throw new Error('Your session has expired. Another device logged into this account.');
    }
    throw new Error(data.error || "Request failed");
  }
  return data;
}

const PAGES = {
  dashboard: Dashboard,
  bestodds: BestOdds,
  valuebets: ValueBets,
  arbitrage: Arbitrage,
  middles: Middles,
  sharpMoney: SharpMoney,
  clv: CLVTracker,
  bankroll: BankrollManager,
  tipsters: TipsterHub,
  telegram: TelegramBot,
  bookmakers: Bookmakers,
  calculators: Calculators,
  education: (props) => <Education {...props} onNavigate={props.onNavigate} />,
  smartParlay: SmartParlay,
  winnerIntelligence: WinnerIntelligence,
  botswanaPulse: BotswanaPulse,
  heatmap: (props) => <HeatMap {...props} onNavigate={props.onNavigate} />,
  profile: Profile,
  billing: Billing,
  admin: Admin,
  account: Account,
  upgrade: Upgrade,
  liveOdds: LiveOdds,
};

export default function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("oe_token"));
  const [page, setPage] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [showLanding, setShowLanding] = useState(!localStorage.getItem("oe_token"));
  const [authMode, setAuthMode] = useState("login");
  const [theme, setTheme] = useState(() => localStorage.getItem('oe_theme') || 'dark');

useEffect(() => {
  document.documentElement.className = theme === 'light' ? 'light' : '';
  localStorage.setItem('oe_theme', theme);
}, [theme]);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
      setInstallPrompt(null);
    }
  };

  useEffect(() => {
    if (token) {
      apiFetch("/auth/me")
        .then((d) => setUser(d.user))
        .catch(() => { setToken(null); localStorage.removeItem("oe_token"); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (tok, usr) => {
    localStorage.setItem("oe_token", tok);
    setToken(tok);
    setUser(usr);
  };

  const logout = () => {
    localStorage.removeItem("oe_token");
    setToken(null);
    setUser(null);
  };

  if (loading) return <Loader />;
if (!user) {
  // Check for password reset token in URL
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get('token');

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {resetToken
        ? <Login mode="reset" onBack={() => setShowLanding(true)} />
        : showLanding
          ? <Landing
              onLogin={() => { setAuthMode("login"); setShowLanding(false); }}
              onRegister={() => { setAuthMode("register"); setShowLanding(false); }}
            />
          : <Login mode={authMode} onBack={() => setShowLanding(true)} />
      }
    </AuthContext.Provider>
  );
}

  const PageComponent = PAGES[page] || Dashboard;

 const isMobile = window.innerWidth < 768;

return (
  <AuthContext.Provider value={{ user, login, logout }}>
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)" }}>
      
      {/* Overlay for mobile */}
      {sidebarOpen && isMobile && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 199,
          }}
        />
      )}

      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 199,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
        }}
      />
)}

      <Sidebar
        currentPage={page}
        onNavigate={(p) => {
          setPage(p);
          if (isMobile) setSidebarOpen(false);
        }}
        isOpen={sidebarOpen}
      />

      <div style={{
        flex: 1,
        marginLeft: (!isMobile && sidebarOpen) ? "260px" : "0",
        transition: "margin .25s ease",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}>
        <Topbar
          page={page}
          user={user}
          onLogout={logout}
          onToggleSidebar={() => setSidebarOpen(o => !o)}
          onNavigate={setPage}
          theme={theme}
          onToggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
      />
        <main style={{ flex: 1, padding: isMobile ? "16px" : "28px 32px", maxWidth: 1400 }}>
          {/* PWA Install Banner */}
          {showInstallBanner && (
            <div style={{
              position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
              zIndex: 300, background: 'var(--bg2)', border: '1px solid var(--accent)',
              borderRadius: 'var(--radius)', padding: '12px 16px',
              display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: '0 4px 24px rgba(0,229,176,0.15)',
              maxWidth: 340, width: 'calc(100% - 32px)',
            }}>
              <img src="/icon-192.png" style={{ width: 36, height: 36, borderRadius: 8 }} alt="OddsEdge BW" />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13 }}>Install OddsEdge BW</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Add to your home screen for quick access</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button onClick={handleInstall} className="btn btn-primary" style={{ fontSize: 11, padding: '5px 12px', whiteSpace: 'nowrap' }}>
                  Install
                </button>
                <button onClick={() => setShowInstallBanner(false)} style={{ fontSize: 10, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center' }}>
                  Not now
                </button>
              </div>
            </div>
          )}
          <PageComponent onNavigate={setPage} />
        </main>
      </div>
    </div>
  </AuthContext.Provider>
);

function Loader() {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "var(--bg)", flexDirection: "column", gap: 16
    }}>
      <div style={{
        width: 48, height: 48, border: "3px solid var(--border)",
        borderTopColor: "var(--accent)", borderRadius: "50%",
        animation: "spin 0.8s linear infinite"
      }} />
      <p style={{ color: "var(--text3)", fontFamily: "var(--font-display)", letterSpacing: 2, fontSize: 12 }}>
        LOADING ODDSEDGE BW
      </p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
}